import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useFeature } from '../../hooks/useFeature';
import { useConversations } from '../../hooks/useConversations';
import apiService from '../../services/apiService';
import { Briefcase, UsersThree, MagnifyingGlassPlus } from '@phosphor-icons/react';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  const now = new Date();
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { collapseNav } = useLayout();
  const { enabled: assessmentsEnabled } = useFeature('assessments_mode');
  const { enabled: salesEnabled } = useFeature('sales_roleplay');
  const { enabled: leadEnabled } = useFeature('lead_roleplay');
  const { conversations, loading: conversationsLoading, hasMore } = useConversations(5);
  const hasConversations = !conversationsLoading && conversations.length > 0;

  const createConversation = async (type, methodology) => {
    try {
      const timestamp = new Date().toLocaleString();
      const title = type === 'sales'
        ? `Sales ${timestamp}`
        : `Leadership ${timestamp}`;

      const response = await apiService.post(
        '/api/v1/conversations',
        {
          conversationType: type,
          methodology,
          title,
          user_id: user.uid,
          tenant_id: user.tenantId
        },
        user
      );

      if (response && response.success && response.data) {
        collapseNav();
        navigate(`/app/chat/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to create conversation. Please try again.');
    }
  };

  return (
    <div className="welcome-screen">
      <div className="welcome-content">
        <h1>{hasConversations ? 'Welcome Back!' : 'Welcome to Scarlett'}</h1>
        <p>
          {hasConversations
            ? 'Click below to resume a recent conversation:'
            : 'Improve your conversational skills through interactive coaching sessions.'}
        </p>
        
        {!conversationsLoading && conversations.length === 0 && (
          <div className="welcome-actions">
            {salesEnabled && (
              <button
                className="welcome-btn sales-btn"
                onClick={() => createConversation('sales', 'real')}
              >
                <span className="btn-icon"><Briefcase weight="light" size={32} /></span>
                <div className="btn-text">
                  <h3>Start a Sales Conversation</h3>
                  <p>Customer and prospect interactions</p>
                </div>
              </button>
            )}

            {leadEnabled && (
              <button
                className="welcome-btn leadership-btn"
                onClick={() => createConversation('leadership', 'real')}
              >
                <span className="btn-icon"><UsersThree weight="light" size={32} /></span>
                <div className="btn-text">
                  <h3>Start a Leadership Conversation</h3>
                  <p>Organizational effectiveness and team management</p>
                </div>
              </button>
            )}

            {assessmentsEnabled && (
              <button
                className="welcome-btn assessment-btn"
                onClick={() => createConversation('leadership', 'basic')}
              >
                <span className="btn-icon"><MagnifyingGlassPlus weight="light" size={32} /></span>
                <div className="btn-text">
                  <h3>Create a Personal Case Study</h3>
                  <p>Work with Scarlett to create a case study</p>
                </div>
              </button>
            )}
          </div>
        )}

        {!conversationsLoading && conversations.length > 0 && (
          <div className="welcome-recent-conversations">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                className="welcome-conversation-row"
                onClick={() => navigate(`/app/chat/${conversation.id}`)}
              >
                <span className="welcome-conversation-title">
                  {conversation.title || 'Untitled Conversation'}
                </span>
                <span className="welcome-conversation-date">
                  {formatDate(conversation.lastActivity || conversation.createdAt)}
                </span>
              </div>
            ))}
            {hasMore && (
              <div
                className="welcome-show-all-link"
                onClick={() => navigate('/app/conversations')}
              >
                Show all my conversations
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeScreen;